import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../../Styles/ArtistRoot.css';
import '../../Styles/Admin.css';

import PODCAST_GENRES from '../../utils/PodcastGender';
import { BsFileEarmarkImage, BsFileEarmarkMusic } from 'react-icons/bs';
import { MdCheckCircle, MdInfoOutline } from 'react-icons/md';
import izenuAPI from '../../services/izenuAPI';

// Cover and audio go up in separate requests, each capped at 40 MB by the
// server (post_max_size). 38 MB of MP3 is about 40 min at 128 kbps.
const AUDIO_MAX_MB = 38;
const COVER_MAX_MB = 5;

const inputCls = 'block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-amber-500 focus:ring-amber-500';

const formatDuration = (ms) => {
    if (!ms) return '';
    const total = Math.round(ms / 1000);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = String(total % 60).padStart(2, '0');
    return h ? `${h}:${String(m).padStart(2, '0')}:${s}` : `${m}:${s}`;
};

// File names on S3. The old generator could index past the alphabet and put
// "undefined" in the name.
const randomId = () => {
    const bytes = new Uint8Array(6);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
};

export default function UploadPodcasts() {
    const distributorId = localStorage.getItem('id');

    const [podcasters, setPodcasters] = useState([]);
    // Opened from a podcaster page ("Carregar episódio"): /podcasts?podcaster=ID.
    const [form, setForm] = useState(() => {
        const p = parseInt(new URLSearchParams(window.location.search).get('podcaster'), 10);
        return { title: '', genre: '', user_id: p > 0 ? String(p) : '' };
    });
    const [audio, setAudio] = useState(null);   // { file, url }
    const [cover, setCover] = useState(null);   // { file, url }
    const [duration, setDuration] = useState(0);
    const [errors, setErrors] = useState({});
    const [progress, setProgress] = useState(null); // null | { step, pct }
    const [done, setDone] = useState(null);

    useEffect(() => {
        izenuAPI.get('/distributor/artist/fetchPodcasters.php?distributor_id=' + distributorId)
            .then(({ data }) => setPodcasters(data.artists || []))
            .catch(err => console.log(err));
    }, [distributorId]);

    useEffect(() => () => { if (audio?.url) URL.revokeObjectURL(audio.url); }, [audio]);
    useEffect(() => () => { if (cover?.url) URL.revokeObjectURL(cover.url); }, [cover]);

    const set = (key) => (e) => {
        setForm(f => ({ ...f, [key]: e.target.value }));
        setErrors(er => ({ ...er, [key]: undefined }));
    };

    const pickAudio = (e) => {
        const file = e.target.files[0];
        e.target.value = '';
        setErrors(er => ({ ...er, audio: undefined }));
        if (!file) return;
        if ((file.name.split('.').pop() || '').toLowerCase() !== 'mp3') {
            setErrors(er => ({ ...er, audio: 'Formato não suportado. Usa um ficheiro MP3.' }));
            return;
        }
        if (file.size > AUDIO_MAX_MB * 1024 * 1024) {
            setErrors(er => ({ ...er, audio: `O ficheiro tem ${(file.size / 1024 / 1024).toFixed(0)} MB. O máximo é ${AUDIO_MAX_MB} MB (cerca de 40 min a 128 kbps). Exporta com um bitrate mais baixo ou divide o episódio.` }));
            return;
        }
        const url = URL.createObjectURL(file);
        const el = new Audio(url);
        setDuration(0);
        el.onloadedmetadata = () => setDuration(Math.round(el.duration * 1000));
        setAudio({ file, url });
    };

    const pickCover = (e) => {
        const file = e.target.files[0];
        e.target.value = '';
        setErrors(er => ({ ...er, cover: undefined }));
        if (!file) return;
        if (!['png', 'jpg', 'jpeg'].includes((file.name.split('.').pop() || '').toLowerCase())) {
            setErrors(er => ({ ...er, cover: 'Formato não suportado. Usa JPG ou PNG.' }));
            return;
        }
        if (file.size > COVER_MAX_MB * 1024 * 1024) {
            setErrors(er => ({ ...er, cover: `A imagem tem ${(file.size / 1024 / 1024).toFixed(1)} MB. O máximo é ${COVER_MAX_MB} MB.` }));
            return;
        }
        setCover({ file, url: URL.createObjectURL(file) });
    };

    const validate = () => {
        const er = {};
        if (!audio) er.audio = 'Escolhe o ficheiro do episódio.';
        if (!cover) er.cover = 'Escolhe a capa.';
        if (!form.user_id) er.user_id = 'Escolhe o podcaster.';
        if (!form.title.trim()) er.title = 'Escreve o título do episódio.';
        if (!form.genre) er.genre = 'Escolhe a categoria.';
        setErrors(er);
        return Object.keys(er).length === 0;
    };

    const send = (url, field, file, key, step) => {
        const fd = new FormData();
        fd.append(field, file);
        fd.append('key', key);
        // No manual Content-Type: axios must set the multipart boundary itself.
        return izenuAPI.post(url, fd, {
            onUploadProgress: (e) => { if (e.total) setProgress({ step, pct: Math.round((e.loaded / e.total) * 100) }); },
        });
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        const id = randomId();
        const d = new Date();
        const stamp = `${d.getUTCFullYear()}_${d.getUTCMonth() + 1}_${d.getUTCDate()}_${id}`;
        const coverExt = (cover.file.name.split('.').pop() || 'jpg').toLowerCase();
        const coverName = `izenu_cover_${stamp}.${coverExt}`;
        const audioName = `izenu_song_${stamp}.mp3`;
        const prefix = `users/${form.user_id}/podcast/`;

        try {
            setProgress({ step: 'capa', pct: 0 });
            const { data: c } = await send(`${process.env.REACT_APP_API_URL}/upload/uploadPodcastCover.php`, 'files', cover.file, prefix + coverName, 'capa');
            if (!c?.response) throw new Error(c?.message || 'Não foi possível enviar a capa.');

            setProgress({ step: 'áudio', pct: 0 });
            const { data: a } = await send(`${process.env.REACT_APP_API_URL}/upload/uploadPodcastAudio.php`, 'files', audio.file, prefix + audioName, 'áudio');
            if (!a?.response) throw new Error(a?.message || 'Não foi possível enviar o áudio.');

            setProgress({ step: 'a gravar', pct: 100 });
            // The episode only counts as published once this row exists; the old
            // screen announced success without waiting for it.
            const { data: r } = await izenuAPI.post('/podcast/cmsInsert.php', {
                title: form.title.trim(), genre: form.genre, user_id: form.user_id,
                audio: audioName, cover: coverName, duration: duration || 0,
            });
            if (!r?.response) throw new Error(r?.message || 'Os ficheiros foram enviados, mas não foi possível gravar o episódio.');

            const podcaster = podcasters.find(p => String(p.user_id) === String(form.user_id));
            setDone({ title: form.title.trim(), podcaster });
            setForm(f => ({ ...f, title: '' }));
            setAudio(null);
            setCover(null);
            setDuration(0);
        } catch (err) {
            console.log(err);
            alert(err?.response?.data?.message || err.message || 'Erro ao carregar o podcast. Tenta novamente.');
        } finally {
            setProgress(null);
        }
    };

    const uploading = progress !== null;

    if (done) {
        return (
            <div className="mx-auto max-w-2xl p-3 sm:p-6 text-left">
                <section className="rounded-xl border border-green-200 bg-white p-6 text-center">
                    <MdCheckCircle size={48} className="mx-auto text-green-600" />
                    <h1 className="mt-2 text-xl font-bold text-gray-900">"{done.title}" publicado</h1>
                    <p className="mt-1 text-sm text-gray-600">O episódio já está disponível no perfil de {done.podcaster?.artist_name || done.podcaster?.name || 'podcaster'}.</p>
                    <div className="mt-5 flex flex-col justify-center gap-2 sm:flex-row">
                        <button type="button" onClick={() => setDone(null)}
                            className="rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-medium !text-white hover:bg-amber-600">Carregar outro episódio</button>
                        {done.podcaster && (
                            <Link to={`/podcaster/${done.podcaster.user_id}`}
                                className="rounded-lg !border !border-solid border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">Abrir o podcaster</Link>
                        )}
                    </div>
                </section>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-5xl p-3 sm:p-6 text-left">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Carregar podcast</h1>
            <p className="mb-5 text-sm text-gray-500">Publica um episódio novo no perfil de um dos teus podcasters.</p>

            <form onSubmit={submit} className="space-y-6">
                {/* 1. Files */}
                <section className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
                    <h2 className="mb-4 text-lg font-semibold text-gray-900">1. Ficheiros</h2>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <div className="sm:col-span-2">
                            <span className="mb-1 block text-sm font-medium text-gray-700">Episódio *</span>
                            <label className={`flex cursor-pointer items-center gap-3 rounded-xl border-2 border-dashed p-4 hover:border-amber-400 ${errors.audio ? 'border-red-300 bg-red-50/40' : 'border-gray-300 bg-gray-50'}`}>
                                <BsFileEarmarkMusic size={32} className="shrink-0 text-gray-400" />
                                <span className="min-w-0 flex-1">
                                    <span className="block truncate text-sm font-medium text-gray-800">{audio ? audio.file.name : 'Escolher ficheiro MP3'}</span>
                                    <span className="block text-xs text-gray-500">{audio ? `${(audio.file.size / 1024 / 1024).toFixed(1)} MB${duration ? ` · ${formatDuration(duration)}` : ''}` : `MP3 · até ${AUDIO_MAX_MB} MB (≈ 40 min a 128 kbps)`}</span>
                                </span>
                                <input type="file" accept=".mp3,audio/mpeg" onChange={pickAudio} className="sr-only" />
                            </label>
                            {audio && <audio controls src={audio.url} className="mt-2 w-full" />}
                            {errors.audio && <p className="mt-1 text-xs text-red-600">{errors.audio}</p>}
                        </div>
                        <div>
                            <span className="mb-1 block text-sm font-medium text-gray-700">Capa *</span>
                            <label className={`flex cursor-pointer items-center gap-3 rounded-xl border-2 border-dashed p-3 hover:border-amber-400 ${errors.cover ? 'border-red-300 bg-red-50/40' : 'border-gray-300 bg-gray-50'}`}>
                                {cover
                                    ? <img src={cover.url} alt="" className="h-12 w-12 shrink-0 rounded-md object-cover" />
                                    : <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-gray-200"><BsFileEarmarkImage size={22} className="text-gray-400" /></span>}
                                <span className="min-w-0 flex-1">
                                    <span className="block truncate text-sm font-medium text-gray-800">{cover ? cover.file.name : 'Escolher capa'}</span>
                                    <span className="block text-xs text-gray-500">JPG/PNG · quadrada, 3000 × 3000 px · até {COVER_MAX_MB} MB</span>
                                </span>
                                <input type="file" accept="image/png,image/jpeg" onChange={pickCover} className="sr-only" />
                            </label>
                            {errors.cover && <p className="mt-1 text-xs text-red-600">{errors.cover}</p>}
                        </div>
                    </div>
                </section>

                {/* 2. Details */}
                <section className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
                    <h2 className="mb-4 text-lg font-semibold text-gray-900">2. Detalhes do episódio</h2>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <label className="block sm:col-span-2">
                            <span className="mb-1 block text-sm font-medium text-gray-700">Podcaster *</span>
                            <select className={inputCls} value={form.user_id} onChange={set('user_id')}>
                                <option value="">Escolhe o podcaster</option>
                                {podcasters.map(p => <option key={p.user_id} value={p.user_id}>{p.artist_name || p.name}</option>)}
                            </select>
                            {errors.user_id
                                ? <span className="mt-1 block text-xs text-red-600">{errors.user_id}</span>
                                : podcasters.length === 0 && <span className="mt-1 block text-xs text-gray-500">Ainda não tens podcasters. <Link to="/podcasters" className="font-medium text-amber-700 hover:underline">Adiciona um</Link> primeiro.</span>}
                        </label>
                        <label className="block sm:col-span-2">
                            <span className="mb-1 block text-sm font-medium text-gray-700">Título *</span>
                            <input className={inputCls} value={form.title} onChange={set('title')} maxLength={100} placeholder="Ex.: Ep. 12 — O futuro da música em Angola" />
                            {errors.title && <span className="mt-1 block text-xs text-red-600">{errors.title}</span>}
                        </label>
                        <label className="block">
                            <span className="mb-1 block text-sm font-medium text-gray-700">Categoria *</span>
                            <select className={inputCls} value={form.genre} onChange={set('genre')}>
                                <option value="">Escolhe uma categoria</option>
                                {PODCAST_GENRES.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                            </select>
                            {errors.genre && <span className="mt-1 block text-xs text-red-600">{errors.genre}</span>}
                        </label>
                    </div>
                    <div className="mt-4 flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
                        <MdInfoOutline size={18} className="mt-0.5 shrink-0" aria-hidden="true" />
                        <p>O episódio fica publicado assim que o envio termina. A capa aparece na app em quadrado; mantém o texto longe das margens.</p>
                    </div>
                </section>

                {/* Submit */}
                <div className="sticky bottom-0 -mx-3 border-t bg-gray-50/95 px-3 py-3 backdrop-blur sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:p-0">
                    {uploading && (
                        <div className="mb-2">
                            <div className="h-2 overflow-hidden rounded-full bg-gray-200">
                                <div className="h-full rounded-full bg-amber-500 transition-all" style={{ width: `${progress.pct}%` }} />
                            </div>
                            <p className="mt-1 text-xs text-gray-600">
                                {progress.step === 'a gravar' ? 'A gravar o episódio…' : `A enviar ${progress.step}… ${progress.pct}%`} Não feches esta página.
                            </p>
                        </div>
                    )}
                    <button type="submit" disabled={uploading}
                        className="w-full rounded-lg bg-amber-500 px-6 py-3 text-sm font-semibold !text-white hover:bg-amber-600 disabled:opacity-60 sm:w-auto">
                        {uploading ? 'A carregar…' : 'Publicar episódio'}
                    </button>
                </div>
            </form>
        </div>
    );
}
